import { useState, useEffect } from 'react';
import { X } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useFilterStore } from '../../stores/filter-store';
import { useOperationFilterStore } from '../../stores/operation-filter-store';

interface FilterModalProps {
  isOpen: boolean;
  onClose: () => void;
  context?: 'dashboard' | 'fulfillment' | 'operation';
  onApplyFilters?: (filters: any) => void;
}

// Separate CountryFilter component for the modal that doesn't trigger immediate filtering
function ModalCountryFilter({ selected, onSelect }: { selected: string[]; onSelect: (code: string) => void; context?: string }) {
  const countries = [
    { code: 'UK', label: 'UK', flag: '/img/country/uk.svg' },
    { code: 'US', label: 'US', flag: '/img/country/us.svg' },
    { code: 'GF', label: 'GF', flag: '/img/country/gf.svg' },
    { code: 'EU', label: 'EU', flag: '/img/country/eu.svg' },
    { code: 'TR', label: 'TR', flag: '/img/country/tr.svg' },
  ];

  return (
    <div className="flex flex-wrap gap-3 items-center">
      {countries.map((country) => {
        const isSelected = selected.includes(country.code);
        return (
          <button
            key={country.code}
            className={`flex min-w-[80px] shrink items-center gap-2 px-2.5 py-1.5 rounded-xl border text-sm font-medium whitespace-nowrap transition-colors 
              ${isSelected
                ? 'bg-primary-100 text-primary-500 border-primary-500'
                : 'bg-white text-gray-700 border-gray-200 hover:bg-gray-50'}
            `}
            onClick={() => onSelect(country.code)}
          >
            {/* Checkbox with rounded background */}
            <div className={`w-5 h-5 shrink-0 aspect-square rounded-md border flex items-center justify-center ${
              isSelected 
                ? 'bg-primary-500 border-primary-500' 
                : 'bg-white border-gray-300'
            }`}>
              {isSelected && <X size={8} className="text-white" />}
            </div>
            
            <span className="shrink-0">{country.label}</span>
            <img 
              src={country.flag} 
              alt={`${country.label} flag`}
              className="w-6 h-6 shrink-0 aspect-square object-contain"
            />
          </button>
        );
      })}
    </div>
  );
}

export default function FilterModal({ isOpen, onClose, context = 'dashboard', onApplyFilters }: FilterModalProps) {
  const { 
    fulfillmentFilters, 
    setFulfillmentDateRange, 
    setFulfillmentMarketplaceFilter,
    // clearFulfillmentFilters
  } = useFilterStore();
  
  const {
    marketplaceFilter: operationMarketplaceFilter,
    filters: operationFilters,
    setFilters: setOperationFilters,
    toggleMarketplace: toggleOperationMarketplace
  } = useOperationFilterStore();
  
  // Local state for filter modal - these don't affect the main filters until applied
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [dateFrom, setDateFrom] = useState<string>('');
  const [dateTo, setDateTo] = useState<string>('');

  // Initialize local state when modal opens
  useEffect(() => {
    if (isOpen) {
      if (context === 'fulfillment') {
        // Convert marketplace format to country codes for display
        setSelectedCountries(
          fulfillmentFilters.marketplaceFilter.map(filter => 
            filter.replace('_mkt', '').toUpperCase()
          )
        );
        setDateFrom(fulfillmentFilters.dateFrom || '');
        setDateTo(fulfillmentFilters.dateTo || '');
      } else if (context === 'operation') {
        // Convert marketplace format to country codes for display
        setSelectedCountries(
          operationMarketplaceFilter.map(filter => 
            filter.replace('_mkt', '').toUpperCase()
          )
        );
        setDateFrom(operationFilters.orderDateFrom || '');
        setDateTo(operationFilters.orderDateTo || '');
      }
    }
  }, [isOpen, context, fulfillmentFilters, operationMarketplaceFilter, operationFilters]);

  // Try to use language context
  let t;
  try {
    const { t: translate } = useLanguage();
    t = translate;
  } catch (error) {
    t = (key: string) => key; // Fallback
  }

  const handleCountrySelect = (countryCode: string) => {
    setSelectedCountries(prev => {
      if (prev.includes(countryCode)) {
        return prev.filter(code => code !== countryCode);
      } else {
        return [...prev, countryCode];
      }
    });
  };

  const handleApplyFilters = () => {
    if (context === 'fulfillment') {
      // Convert country codes back to marketplace format
      const marketplaceFilters = selectedCountries.map(country => 
        `${country.toLowerCase()}_mkt`
      );
      
      // Apply the filters to the store
      setFulfillmentMarketplaceFilter(marketplaceFilters);
      setFulfillmentDateRange(dateFrom || null, dateTo || null);
      
      // Call the callback if provided
      onApplyFilters?.({
        marketplaceFilter: marketplaceFilters,
        dateFrom: dateFrom || null,
        dateTo: dateTo || null
      });
         } else if (context === 'operation') {
       // Convert country codes back to marketplace format
       const marketplaceFilters = selectedCountries.map(country => 
         `${country.toLowerCase()}_mkt`
       );
       
       // Clear existing marketplace filters first
       operationMarketplaceFilter.forEach(filter => {
         toggleOperationMarketplace(filter);
       });
       
       // Add new marketplace filters
       marketplaceFilters.forEach(filter => {
         toggleOperationMarketplace(filter);
       });
       
       // Apply the date filters to the operation store
       setOperationFilters({
         ...operationFilters,
         orderDateFrom: dateFrom || '',
         orderDateTo: dateTo || ''
       });
       
       // Call the callback if provided
       onApplyFilters?.({
         marketPlaceType: marketplaceFilters.join(','),
         orderDateFrom: dateFrom || '',
         orderDateTo: dateTo || ''
       });
     }
    
    onClose();
  };

  const handleClearFilters = () => {
    setSelectedCountries([]);
    setDateFrom('');
    setDateTo('');
  };

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-40"
          onClick={onClose}
        />
      )}

      {/* Modal */}
      <div
        className={`fixed top-0 bg-white shadow-2xl transition-transform duration-300 ease-in-out z-50 ${
          isOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{
          width: '656px',
          height: '960px',
          right: '0px',
          top: '0px'
        }}
      >
        {/* Header */}
        <div className="bg-secondary-500 text-white p-4 flex justify-between items-center">
          <h2 className="text-lg font-semibold">{t('common.filter') || 'Filter Order'}</h2>
          <button
            onClick={onClose}
            className="bg-[#426198] px-2 py-2 rounded-full text-white hover:text-gray-300 transition-colors"
          >
            <X size={26} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6" style={{ height: 'calc(960px - 80px)' }}>
          {/* Filter by Marketplace Zone */}
          <div className="mb-8">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              {t('common.filterByMarketplaceZone') || 'Filter by Marketplace Zone'}
            </h3>
                         <ModalCountryFilter 
               selected={selectedCountries}
               onSelect={handleCountrySelect}
               context={context}
             />
          </div>

          {/* Filter By Date */}
          <div className="mb-8">
            <h3 className="text-base font-semibold text-gray-900 mb-4">
              {t('common.filterByDate') || 'Filter By Date'}
            </h3>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.from') || 'From'}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateFrom}
                    onChange={(e) => setDateFrom(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="mm/dd/yyyy"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('common.to') || 'To'}
                </label>
                <div className="relative">
                  <input
                    type="date"
                    value={dateTo}
                    onChange={(e) => setDateTo(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-yellow-500 focus:border-yellow-500"
                    placeholder="mm/dd/yyyy"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3 pt-4 border-t border-gray-200">
            <button
              onClick={handleClearFilters}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 font-medium hover:bg-gray-50 transition-colors"
            >
              {t('common.resetFilter') || 'Reset Filter'}
            </button>
            <button
              onClick={handleApplyFilters}
              className="flex-1 px-4 py-2 bg-yellow-500 text-white rounded-lg font-medium hover:bg-yellow-600 transition-colors"
            >
              {t('common.filter') || 'Filter'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
